// relativeTime.ts — Sprint 118 (ADR-111), REWRITTEN Sprint 145 (ADR-140).
// ----------------------------------------------------------------------------
// WHAT THIS FILE USED TO SAY, AND WHY IT WAS WRONG
//
// Sprint 118 fixed R8-LOW-2 ("posted now" rendering as "in 4 hours") by parsing
// the backend's offset-less `LocalDateTime` strings as UTC. Its header stated
// the reasoning outright: *"The server clock is UTC, so the value is really
// UTC."* That was true when written.
//
// It is not true now. The R11-5 remediation set `TZ=America/Toronto` on
// `condo-backend` (docker-compose.yml), and nothing pointed at this file. The
// helper carried on subtracting a UTC→local conversion that no longer applied,
// so it over-corrected by exactly the EDT offset and an announcement posted
// seconds earlier read **"4 hours ago"** (R14-8). ADR-136's own verification is
// the receipt: `createdAt = 2026-07-19T20:27:36`, recorded at 20:27 EDT.
//
// The instructive part is not the four hours. It is that a *correct* fix in one
// sprint invalidated a *correct* fix in another, silently, because the contract
// between them lived in a comment instead of in the data.
//
// WHAT REPLACED IT
//
// Sprint 145 moved the fix to the source. `JacksonTimeConfig` (backend) now
// serializes every `LocalDateTime` as RFC 3339 **with the server's offset**:
//
//     2026-07-19T20:27:36-04:00     ← a moment; unambiguous, self-describing
//     2026-07-19                    ← a calendar date; NO zone, ever
//
// So this file **loses** its zone guesswork rather than gaining a smarter guess.
// `dayjs(ts)` handles an offset-bearing string natively — it resolves to the
// real instant and renders in the viewer's zone. There is no longer an
// out-of-band assumption here for a future sprint to invalidate.
//
// Offset-LESS input is still tolerated, because the backend accepts and (for a
// stale cached bundle, a fixture, or a non-condo vertical) may still emit it.
// It is now read as **the viewer's local time**, which is what `dayjs()` does by
// default and what an offset-less string means to every other JS date parser.
// That is a strictly better fallback than the old UTC assumption: when server
// and viewer share a zone — the overwhelmingly common case for a single-city
// Canadian condo — it is exactly right, and when they don't, it degrades to a
// zone offset rather than to a wrong absolute instant plus one.
//
// The future-skew clamp is KEPT, and is still doing real work: server and
// client clocks drift, and a record written milliseconds ago can legitimately
// carry a timestamp a hair in the future. "in a few seconds" is jarring; "just
// now" is honest.
//
// Use this everywhere instead of `dayjs(ts).fromNow()`.
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

/** Clamp window: values up to this far in the future read as "just now". */
const FUTURE_SKEW_MS = 60_000; // 60s

/**
 * Parse a backend timestamp and return a `dayjs` instance in the VIEWER's zone.
 *
 * - RFC 3339 with an offset (`…-04:00`, `…Z`) — the offset is honoured and the
 *   instant is rendered in the viewer's zone. This is the contract since
 *   Sprint 145.
 * - Offset-less (`2026-07-19T20:27:36`) — read as viewer-local. See the header
 *   for why this is the right fallback rather than a UTC assumption.
 */
export function parseServerTime(ts: string | number | Date | null | undefined) {
  return dayjs(ts as never);
}

/**
 * Relative time ("a few seconds ago", "2 hours ago") for a backend timestamp,
 * with future-skew clamped to "just now".
 */
export function fromNowServer(ts: string | number | Date | null | undefined): string {
  if (ts === null || ts === undefined || ts === '') return '';
  const m = parseServerTime(ts);
  if (!m.isValid()) return '';
  const deltaMs = m.valueOf() - Date.now();
  if (deltaMs > 0 && deltaMs <= FUTURE_SKEW_MS) return 'just now';
  return m.fromNow();
}
